import { useLogoutMutation } from '@codezo/graphql';
import { AuthLoadingScreen } from './AuthLoadingScreen';
import { useEffect } from 'react';
import { clearUserRole } from '../utils';
import { devWarn } from '../utils/devLog';

interface LogoutCallbackProps {
	setUser: (user: null) => void;
	setAccessToken: (token: null) => void;
	token: string | null;
}

export function LogoutCallback({ setUser, setAccessToken, token }: LogoutCallbackProps) {
	const [logoutMutation] = useLogoutMutation();

	useEffect(() => {
		let isLoggedOut = false;

		const performLogout = async () => {
			if (isLoggedOut) return; // Prevent double execution
			isLoggedOut = true;

			try {
				// Refresh token travels as a cookie sent automatically by the browser;
				// the resolver reads it from the cookie and clears it via Set-Cookie.
				// We pass the access token so the server can revoke it server-side.
				await logoutMutation({
					variables: {
						input: {
							token: token ?? '',
						},
					},
				});
			} catch (error) {
				// Continue with logout even if mutation fails
				devWarn('Logout mutation failed:', error);
			}

			// Clear local state
			setUser(null);
			setAccessToken(null);
			clearUserRole();

			// Redirect to public app after a brief delay with logout parameter
			setTimeout(() => {
				const publicAppUrl = import.meta.env.VITE_PUBLIC_APP_URL || '/';
				window.location.replace(`${publicAppUrl}?logout=true`);
			}, 1500);
		};

		performLogout();
		// eslint-disable-next-line react-hooks/exhaustive-deps -- run logout once on mount
	}, []); // Empty dependency array to run only once

	return (
		<AuthLoadingScreen
			title="Logging out..."
			description="You are being securely logged out."
		/>
	);
}
